/**
 * TableToolbar Component
 * Integrated toolbar for DataTable with search, filters, refresh, and column configuration
 */

import React, { useState, useCallback, useMemo, memo } from 'react';
import { Space, Popover, Badge, Checkbox, Flex } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';

import { Button } from '../Button';
import { Input } from '../Input';
import { Tag } from '../Tag';
import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { useDebouncedSearch } from './useDebouncedSearch';
import { FilterFieldsList } from './FilterFieldsList';
import { renderFilterFieldControl, isFilterValueActive, toFilterChips } from './filterFieldControl';
import type { FilterField, FilterValues, ColumnConfig } from './DataTable.types';

const EMPTY_FILTER_VALUES: FilterValues = {};
const EMPTY_COLUMN_DRIVEN_FIELDS: FilterField[] = [];

export interface TableToolbarComponentProps {
    columns: ColumnConfig<unknown>[];
    visibleColumns: string[];
    onVisibleColumnsChange: (columns: string[]) => void;
    defaultVisibleColumns: string[];
    searchValue?: string;
    searchPlaceholder?: string;
    onSearchChange?: (value: string) => void;
    searchDebounce?: number;
    filterFields?: FilterField[];
    /**
     * EAA-1666: fields derived from `ColumnConfig.filter` (see `DataTable.tsx`).
     * Rendered as individual buttons — one per field — instead of the single
     * combined "Filters" popover `filterFields` uses, matching the column
     * config-driven filter UI. Kept as a separate list (not merged into
     * `filterFields`) so the two render paths never bleed into each other.
     */
    columnDrivenFilterFields?: FilterField[];
    filterValues?: FilterValues;
    onFilterChange?: (key: string, value: unknown) => void;
    onReset?: () => void;
    onRefresh?: () => void;
    loading?: boolean;
    /** Extra content rendered alongside the right-side toolbar buttons */
    extra?: React.ReactNode;
}

interface ColumnFilterButtonProps {
    field: FilterField;
    value: unknown;
    onChange?: (key: string, value: unknown) => void;
}

/**
 * EAA-1666: one button per column-driven filter field — shows the field
 * label plus its current value(s) as inline chips (e.g. "Ứng dụng [lms][ach]")
 * and opens a small popover with just that field's control on click. Memoized
 * since it sits in a `.map()` inside `TableToolbar` and must not re-render for
 * unrelated toolbar state changes (search typing, other fields' popovers).
 */
const ColumnFilterButtonInner: React.FC<ColumnFilterButtonProps> = ({ field, value, onChange }) => {
    const { common } = useUIText();
    const [open, setOpen] = useState(false);

    const handleChange = useCallback(
        (nextValue: unknown) => onChange?.(field.key, nextValue),
        [field.key, onChange]
    );

    const chips = useMemo(() => toFilterChips(field, value), [field, value]);

    const content = useMemo(
        () => (
            <div className="table-toolbar-column-filter-popover" style={{ width: 240 }}>
                {renderFilterFieldControl(field, value, handleChange, {
                    all: common.all,
                    startDate: common.startDate,
                    endDate: common.endDate,
                })}
            </div>
        ),
        [field, value, handleChange, common.all, common.startDate, common.endDate]
    );

    return (
        <Popover
            content={content}
            trigger="click"
            placement="bottomLeft"
            open={open}
            onOpenChange={setOpen}
        >
            <Button className="table-toolbar-column-filter-button">
                <Space size={4}>
                    <span>{field.label}</span>
                    {chips.map((chip) => (
                        <Tag
                            key={chip}
                            className="table-toolbar-column-filter-chip"
                            bordered={false}
                        >
                            {chip}
                        </Tag>
                    ))}
                </Space>
            </Button>
        </Popover>
    );
};

const ColumnFilterButton = memo(ColumnFilterButtonInner);
ColumnFilterButton.displayName = 'ColumnFilterButton';

const TableToolbarInner: React.FC<TableToolbarComponentProps> = ({
    columns,
    visibleColumns,
    onVisibleColumnsChange,
    defaultVisibleColumns,
    searchValue = '',
    searchPlaceholder,
    onSearchChange,
    searchDebounce = 300,
    filterFields = [],
    columnDrivenFilterFields = EMPTY_COLUMN_DRIVEN_FIELDS,
    filterValues: filterValuesProp,
    onFilterChange,
    onReset,
    onRefresh,
    loading = false,
    extra,
}) => {
    const filterValues = filterValuesProp ?? EMPTY_FILTER_VALUES;
    const colors = useUIColors();
    const { common } = useUIText();
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [columnPopoverOpen, setColumnPopoverOpen] = useState(false);
    const { localValue: localSearchValue, handleChange: handleSearchChange } = useDebouncedSearch(
        searchValue,
        onSearchChange,
        searchDebounce
    );

    // Filter out hidden columns' filters
    const visibleFilterFields = useMemo(() => {
        return filterFields.filter((field) => {
            if (!field.columnKey) return true;
            return visibleColumns.includes(field.columnKey);
        });
    }, [filterFields, visibleColumns]);

    // EAA-1666: same visibility rule, applied to the new column-driven list.
    // Kept as its own memo (not merged into `visibleFilterFields`) since the
    // two lists render through entirely different toolbar UI paths.
    const visibleColumnDrivenFilterFields = useMemo(() => {
        return columnDrivenFilterFields.filter((field) => {
            if (!field.columnKey) return true;
            return visibleColumns.includes(field.columnKey);
        });
    }, [columnDrivenFilterFields, visibleColumns]);

    // Count active filters (non-empty values, only for visible filters) — the
    // legacy "Filters" button's own badge, unchanged in scope: it only ever
    // counted the legacy `filterFields` list.
    const activeFiltersCount = useMemo(() => {
        const visibleFilterKeys = visibleFilterFields.map((f) => f.key);
        return Object.entries(filterValues).filter(
            ([key, v]) => visibleFilterKeys.includes(key) && isFilterValueActive(v)
        ).length;
    }, [filterValues, visibleFilterFields]);

    // EAA-1666: top-level "Xoá bộ lọc" visibility — scoped to column-driven
    // fields ONLY. The legacy `filterFields` popover already has its own
    // reset link (`FilterFieldsList`'s onReset), and existing consumers of
    // that prop must see zero new toolbar UI — so this button only appears
    // when a table actually has column-driven fields (which none of the 9
    // legacy `filterFields` consumers do), never merely because a legacy
    // filter is active.
    const hasAnyActiveFilter = useMemo(() => {
        return visibleColumnDrivenFilterFields.some((f) =>
            isFilterValueActive(filterValues[f.key])
        );
    }, [filterValues, visibleColumnDrivenFilterFields]);

    const handleResetFilters = useCallback(() => {
        onReset?.();
        setFilterPopoverOpen(false);
    }, [onReset]);

    // Clear filters for columns that were just hidden — covers both the
    // legacy `filterFields` list and the new column-driven one, since a
    // column-driven filter is just as tied to its column's visibility.
    const clearFiltersForHiddenColumns = useCallback(
        (newVisibleColumns: string[]) => {
            if (!onFilterChange) return;
            [...filterFields, ...columnDrivenFilterFields].forEach((field) => {
                if (!field.columnKey) return;
                const wasVisible = visibleColumns.includes(field.columnKey);
                const isNowVisible = newVisibleColumns.includes(field.columnKey);
                if (wasVisible && !isNowVisible) {
                    onFilterChange(field.key, null);
                }
            });
        },
        [filterFields, columnDrivenFilterFields, visibleColumns, onFilterChange]
    );

    // Checkbox.Group only emits values for hideable columns. Merge with fixed
    // (hideable === false) keys in definition order so the table keeps STT etc.
    const handleColumnToggle = useCallback(
        (checkedValues: (string | number | boolean)[]) => {
            const checkedSet = new Set(checkedValues.map(String));
            const newVisible = columns
                .filter((col) => col.hideable === false || checkedSet.has(col.key))
                .map((col) => col.key);
            clearFiltersForHiddenColumns(newVisible);
            onVisibleColumnsChange(newVisible);
        },
        [columns, onVisibleColumnsChange, clearFiltersForHiddenColumns]
    );

    const handleResetColumns = useCallback(() => {
        clearFiltersForHiddenColumns(defaultVisibleColumns);
        onVisibleColumnsChange(defaultVisibleColumns);
        setColumnPopoverOpen(false);
    }, [onVisibleColumnsChange, defaultVisibleColumns, clearFiltersForHiddenColumns]);

    const hideableColumns = useMemo(
        () => columns.filter((col) => col.hideable !== false),
        [columns]
    );

    const hideableVisibleKeys = useMemo(
        () =>
            hideableColumns.filter((col) => visibleColumns.includes(col.key)).map((col) => col.key),
        [hideableColumns, visibleColumns]
    );

    const filterContent = useMemo(
        () => (
            <FilterFieldsList
                fields={visibleFilterFields}
                values={filterValues}
                onChange={onFilterChange}
                onReset={onReset ? handleResetFilters : undefined}
            />
        ),
        [visibleFilterFields, filterValues, onFilterChange, onReset, handleResetFilters]
    );

    const columnContent = useMemo(
        () => (
            <Space
                className="table-toolbar-column-content"
                direction="vertical"
                style={{ width: 200 }}
            >
                <Checkbox.Group
                    value={hideableVisibleKeys}
                    onChange={handleColumnToggle}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                    {hideableColumns.map((col) => (
                        <Checkbox key={col.key} value={col.key}>
                            {col.title}
                        </Checkbox>
                    ))}
                </Checkbox.Group>
                <Button
                    className="table-toolbar-reset-columns"
                    type="link"
                    onClick={handleResetColumns}
                    style={{ padding: 0 }}
                >
                    {common.resetToDefault}
                </Button>
            </Space>
        ),
        [
            hideableColumns,
            hideableVisibleKeys,
            handleColumnToggle,
            handleResetColumns,
            common.resetToDefault,
        ]
    );

    return (
        <Flex
            className="table-toolbar"
            justify="space-between"
            align="center"
            gap="middle"
            style={{ marginBottom: 16 }}
            wrap="wrap"
        >
            <Space className="table-toolbar-left" wrap>
                {onSearchChange && (
                    <Input
                        className="table-toolbar-search"
                        placeholder={searchPlaceholder || common.search}
                        prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
                        value={localSearchValue}
                        onChange={handleSearchChange}
                        style={{ width: 280 }}
                        allowClear
                    />
                )}

                {filterFields.length > 0 && (
                    <Popover
                        content={filterContent}
                        title={common.filters}
                        trigger="click"
                        placement="bottomLeft"
                        open={filterPopoverOpen}
                        onOpenChange={setFilterPopoverOpen}
                    >
                        <Badge count={activeFiltersCount} size="small">
                            <Button
                                className="table-toolbar-filter-button"
                                icon={<FilterOutlined />}
                            >
                                {common.filters}
                            </Button>
                        </Badge>
                    </Popover>
                )}

                {/*
                 * EAA-1666: column-driven fields each get their own button
                 * (not the combined popover above) — one per filterable
                 * column, matching the reference toolbar's per-field layout.
                 */}
                {visibleColumnDrivenFilterFields.map((field) => (
                    <ColumnFilterButton
                        key={field.key}
                        field={field}
                        value={filterValues[field.key]}
                        onChange={onFilterChange}
                    />
                ))}

                {onReset && hasAnyActiveFilter && (
                    <Button className="table-toolbar-reset-all" type="link" onClick={onReset}>
                        {common.resetFilters}
                    </Button>
                )}
            </Space>

            <Space className="table-toolbar-right">
                {extra}

                {onRefresh && (
                    <Button
                        className="table-toolbar-refresh"
                        icon={<ReloadOutlined spin={loading} />}
                        onClick={onRefresh}
                        disabled={loading}
                        title={common.refresh}
                    />
                )}

                {hideableColumns.length > 0 && (
                    <Popover
                        content={columnContent}
                        title={common.showColumns}
                        trigger="click"
                        placement="bottomRight"
                        open={columnPopoverOpen}
                        onOpenChange={setColumnPopoverOpen}
                    >
                        <Button
                            className="table-toolbar-columns"
                            icon={<SettingOutlined />}
                            title={common.columns}
                        />
                    </Popover>
                )}
            </Space>
        </Flex>
    );
};

TableToolbarInner.displayName = 'TableToolbar';

export const TableToolbar = memo(TableToolbarInner);

export default TableToolbar;
